/// <reference types="vite-plugin-svgr/client" />
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LogoWithTitlePL from "@monorepo/assets/src/svg/logos/easy-bill-with-title.svg?react";
import { Loader } from "@monorepo/components";
import { LANGUAGE } from "@monorepo/constants";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { AppPhoneIcon, HelpIcon, MailIcon, ProfileIcon, ReferralIcon } from "@monorepo/icons";
import { useSupportQuery } from "@monorepo/services/api/misc";
import theme from "@monorepo/styles/theme";
import { LanguagesEnum } from "@monorepo/types";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import LogoLT from "assets/img/logo.svg?react";

import { IconsBoxStyle, IconStyle, StyledButton, StyledLink } from "./MobileMenu.styles";

interface IProps {
  onToggle: (openState: boolean) => void;
  open: boolean;
}

const MobileMenu = ({ open, onToggle }: IProps) => {
  const { t } = useTranslation();
  const { data: contactInfo, isFetching } = useSupportQuery();

  if (isFetching && open) {
    return <Loader />;
  }

  const toggleDrawer = (openState: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
    ) {
      return;
    }
    onToggle(openState);
  };
  return (
    <Drawer
      sx={{ zIndex: 3 }}
      PaperProps={{
        elevation: 0,
        sx: {
          width: "100vw",
        },
      }}
      anchor="right"
      open={open}
      onClose={() => toggleDrawer(false)}
    >
      <Box
        sx={{
          width: "100vw",
          marginBottom: "80px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          rowGap: theme.spacing(6),
        }}
      >
        <Link onClick={() => onToggle(false)} to={routeConfig.dashboard.payments.self}>
          {LANGUAGE === LanguagesEnum.PL && <LogoWithTitlePL />}
          {LANGUAGE === LanguagesEnum.LT && <LogoLT />}
        </Link>
        <Box sx={IconsBoxStyle}>
          <StyledLink onClick={() => onToggle(false)} to={routeConfig.dashboard.profile.self}>
            <StyledButton>
              <ProfileIcon sx={IconStyle} />
              <Typography component="span">{t("navigation.profile")}</Typography>
            </StyledButton>
          </StyledLink>
          <Divider orientation="vertical" flexItem sx={{ height: "100%" }} />
          <StyledLink onClick={() => onToggle(false)} to={routeConfig.dashboard.help.self}>
            <StyledButton>
              <HelpIcon sx={IconStyle} />
              <Typography component="span">{t("navigation.help")}</Typography>
            </StyledButton>
          </StyledLink>
          <Box sx={{ gridColumn: "1 / span 3", width: "100%" }}>
            <Divider />
          </Box>
          <StyledLink onClick={() => onToggle(false)} to={routeConfig.dashboard.referral.self}>
            <StyledButton>
              <ReferralIcon sx={IconStyle} />
              <Typography component="span">{t("navigation.referral")}</Typography>
            </StyledButton>
          </StyledLink>
          <Divider orientation="vertical" flexItem sx={{ height: "100%" }} />
        </Box>
        <Box
          sx={{
            "& a": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              rowGap: theme.spacing(1),
              padding: theme.spacing(2),
              textDecoration: "none",
              color: theme.palette.text.primary,
              transition: `all ${theme.transitions.duration.standard / 1000}s ${theme.transitions.easing.easeInOut}`,
              "&:hover": {
                color: theme.palette.primary.main,
                transition: `all ${theme.transitions.duration.standard / 1000}s ${theme.transitions.easing.easeInOut}`,
              },
            },
          }}
        >
          <a href={`tel:${contactInfo?.phone}`}>
            <AppPhoneIcon sx={{ stroke: theme.palette.primary.main }} />
            <Typography sx={{ color: theme.palette.text.disabled }} component="span">
              {contactInfo?.works_at}
            </Typography>
            <Typography component="strong">{contactInfo?.phone}</Typography>
          </a>
          <a href={`mailto:${contactInfo?.email}`}>
            <MailIcon sx={{ stroke: theme.palette.primary.main }} />
            <Typography sx={{ color: theme.palette.text.disabled }} component="span">
              {contactInfo?.response_time}
            </Typography>
            <Typography component="strong">{contactInfo?.email}</Typography>
          </a>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MobileMenu;
